import { v4 as uuidv4 } from "uuid"
import {
    DeleteMessageBody,
    DialogBadge,
    DialogContext,
    DialogPerson,
    EditMessageBody,
    IGetOperatorClientBadgesResponse,
    TransformedDialogContext
} from "../../models/Dialogs/dialog"
import {
    ApiDeletedMessage,
    ApiEditMessage,
    ApiMessage,
    GetUpdatedMessagesRequest,
    isApiMessage,
    MessageCase,
    MessageDirection,
    MessageStatus,
    MessageTextType,
    MessageType,
    QuotedMessage,
    SendInternalMessageRequest,
    SendMessageRequest,
    TMessageWithEditHistory
} from "../../models/Dialogs/message"
import store from "../../store/store"
import { Dispatch } from "../../utility/common/storeHelper"
import { convertRequestToResponse } from "../../utility/message/convertRequestToResponse"
import { dialogsApi, DialogsApiEndpoints, updateDialogBadges } from "../controllers/dialogs"
import { CONTEXT_TRANSLATE_INFO_KEY } from "../../components/Dialogs/DialogMessageInput/components/DialogTranslationButtons/DialogTranslationButtons.const"

export const refetchDialogBadges = () => {
    return dialogsApi.endpoints.getDialogBadges.initiate(undefined, {
        forceRefetch: true
    })
}

export const refetchActiveDialogsCount = () => {
    return dialogsApi.endpoints.getActiveDialogsCount.initiate(undefined, {
        forceRefetch: true
    })
}

export const upsertDialogBadges = (newData: DialogBadge[]) => {
    return dialogsApi.util.upsertQueryData(DialogsApiEndpoints.getDialogBadges, undefined, newData)
}

export const updateOperatorClientsBadges = (client: DialogPerson, projectId: string) => {
    return dialogsApi.util.updateQueryData(DialogsApiEndpoints.getOperatorClientsBadges, undefined, oldState => {
        const clientEntry: IGetOperatorClientBadgesResponse = {
            OmniUserId: client.OmniUserId,
            Firstname: client.Firstname,
            Lastname: client.Lastname,
            Middlename: client.Middlename,
            Picture: client.Picture,
            ProjectId: projectId
        }

        const oldStateFiltered = oldState.filter(operatorClient => operatorClient.OmniUserId !== client.OmniUserId)

        const newState = [clientEntry, ...oldStateFiltered]
        Object.assign(oldState, newState)
    })
}

export const updateCurrentDialogBadgeWithOutgoing = (dialogId: string) => {
    return updateDialogBadges(oldState => {
        const idxIntoList = oldState.findIndex(x => x.Id === dialogId)

        if (idxIntoList > -1) {
            oldState[idxIntoList] = {
                ...oldState[idxIntoList],
                UnreadMessages: 0,
                IsReplied: true,
                Stat: {
                    ...oldState[idxIntoList].Stat,
                    LastOutgoingMessageSent: Date.now(),
                    LastIncomingMessageSent: 0
                }
            }
        }
    })
}

export const updateDialogBadge = (badgeIndex: number, badge: DialogBadge, message: ApiMessage) => {
    const tDialogsBadgesDescription = "dialogs:dialogs-list.description.attachment"

    return updateDialogBadges(oldState => {
        const updatedBadge = {
            ...badge,
            UnreadMessages: badge.UnreadMessages + 1
        }

        const newPreview = message.Fields.Text
        const newAttachments = message.Fields.Attachments

        if (newPreview) {
            updatedBadge.Preview = newPreview
        } else if (newAttachments.length) {
            updatedBadge.Preview = tDialogsBadgesDescription
        }

        if (message.Fields.Direction === MessageDirection.Incoming) {
            updatedBadge.Stat = {
                ...badge.Stat,
                LastOutgoingMessageSent: 0,
                LastIncomingMessageSent: Date.now()
            }
        }

        if (
            message.Fields.Direction === MessageDirection.Outgoing &&
            message.Fields.MessageType !== MessageType.InternalOperatorMessage
        ) {
            updatedBadge.Stat = {
                ...badge.Stat,
                LastOutgoingMessageSent: Date.now(),
                LastIncomingMessageSent: 0
            }
        }

        oldState[badgeIndex] = updatedBadge
    })
}

export const selectDialogBadges = () => {
    const dialogBadgesSelector = dialogsApi.endpoints.getDialogBadges.select()
    const { data: dialogBadges } = dialogBadgesSelector(store.getState())
    return dialogBadges
}

function updateQuotedMessageIfMatches(
    message: TMessageWithEditHistory,
    id: string,
    updater: (quoted: QuotedMessage) => QuotedMessage
) {
    if (isApiMessage(message) && message.Fields.QuotedMessage && message.Fields.QuotedMessage.Id === id) {
        message.Fields = {
            ...message.Fields,
            QuotedMessage: updater(message.Fields.QuotedMessage)
        }
    }
}

export const addDeleteEventToMessages = (
    dispatch: Dispatch,
    currentMessagePagination: GetUpdatedMessagesRequest,
    deletedMessage: DeleteMessageBody
) => {
    dispatch(
        dialogsApi.util.updateQueryData("getDialogMessages", currentMessagePagination, draft => {
            draft.Messages.forEach(message => {
                updateQuotedMessageIfMatches(message, deletedMessage.Id, () => ({
                    Id: deletedMessage.Id,
                    IsDeleted: true,
                    AddedDate: 0,
                    Direction: MessageDirection.Incoming
                }))
            })

            const deleteMessageRecord: ApiDeletedMessage = {
                Case: MessageCase.DeletedByOperator,
                Fields: {
                    DeletedMessageId: deletedMessage.Id,
                    DialogId: deletedMessage.DialogId,
                    Id: uuidv4(),
                    Timestamp: Date.now()
                }
            }

            draft.Messages.push(deleteMessageRecord)
        })
    )
}

// Именно обновление поля сообщения, без удаления и заменой его новым сообщением
export const updateMessageMeta = (
    dispatch: Dispatch,
    currentMessagePagination: GetUpdatedMessagesRequest,
    messageId: string,
    updatedMeta: Record<string, string>
) => {
    dispatch(
        dialogsApi.util.updateQueryData("getDialogMessages", currentMessagePagination, draft => {
            const index = draft.Messages.findIndex(message => message.Fields.Id === messageId)

            if (index !== -1) {
                const message = draft.Messages[index]

                if (isApiMessage(message)) {
                    const fields = draft.Messages[index].Fields

                    if (fields && "Meta" in fields && updatedMeta) {
                        fields.Meta = updatedMeta
                    }
                }
            }
        })
    )
}

export const addEditEventToMessages = (
    dispatch: Dispatch,
    currentMessagePagination: GetUpdatedMessagesRequest,
    operatorId: string,
    editMessage: EditMessageBody,
    idToCreate: string
) => {
    dispatch(
        dialogsApi.util.updateQueryData("getDialogMessages", currentMessagePagination, draft => {
            draft.Messages.forEach(message => {
                updateQuotedMessageIfMatches(message, editMessage.Id, quotedMessage => ({
                    ...quotedMessage,
                    Attachments: editMessage.Attachments ? editMessage.Attachments : [],
                    Text: editMessage.Text,
                    Meta: editMessage.Meta
                }))
            })

            const editMessageRecord: ApiEditMessage = {
                Case: MessageCase.EditedByOperator,
                Fields: {
                    Id: idToCreate,
                    MessageType: 1,
                    TextType: MessageTextType.Markdown,
                    Direction: 1,
                    Sender: { Id: operatorId },
                    DialogId: editMessage.DialogId,
                    AddedDate: Date.now(),
                    Context: [[]],
                    Status: 0,
                    EditedMessageId: editMessage.Id,
                    Attachments: editMessage.Attachments ? editMessage.Attachments : [],
                    Sticker: editMessage.Sticker,
                    ChannelId: editMessage.ChannelId,
                    Meta: {},
                    Actions: [],
                    Text: editMessage.Text
                }
            }

            draft.Messages.push(editMessageRecord)
        })
    )
}

export const updateDialogMessagesQuery = (
    dispatch: Dispatch,
    currentMessagePagination: GetUpdatedMessagesRequest,
    messageIdToDelete: string,
    messageIdToCreate: string,
    updateMessage: SendMessageRequest | SendInternalMessageRequest,
    operatorId: string | undefined,
    quotedMessage?: QuotedMessage,
    status?: MessageStatus,
    isInternal = false,
    dialogId?: string
) => {
    if (!operatorId) {
        return
    }

    const message = convertRequestToResponse(updateMessage, operatorId, messageIdToCreate, status, isInternal, dialogId)

    if (quotedMessage) {
        message.Fields.QuotedMessage = quotedMessage
    }

    return dispatch(
        dialogsApi.util.updateQueryData("getDialogMessages", currentMessagePagination, draft => {
            const index = draft.Messages.findIndex(message => message.Fields.Id === messageIdToDelete)
            if (index !== -1) {
                draft.Messages.splice(index, 1)
            }
            draft.Messages.push(message)
        })
    )
}

export const updateDialogMessageQueryId = (
    dispatch: Dispatch,
    currentMessagePagination: GetUpdatedMessagesRequest,
    messageIdToDelete: string,
    messageIdToCreate: string,
    operatorId: string | undefined
) => {
    if (!operatorId) return
    return dispatch(
        dialogsApi.util.updateQueryData("getDialogMessages", currentMessagePagination, draft => {
            const index = draft.Messages.findIndex(message => message.Fields.Id === messageIdToDelete)
            if (index !== -1) {
                const message = draft.Messages[index]
                draft.Messages.splice(index, 1)
                message.Fields.Id = messageIdToCreate
                draft.Messages.push(message)
            }
        })
    )
}

export const transformDialogContextResponse = (context: DialogContext): TransformedDialogContext => {
    const languageContextElement = context.find(element => element[0] === CONTEXT_TRANSLATE_INFO_KEY)
    const [, isAutoTranslateEnabled, , operatorLanguage] = languageContextElement || [
        CONTEXT_TRANSLATE_INFO_KEY,
        undefined,
        "",
        "",
        ""
    ]
    const contextQueueId = context.find(element => element[0] === "routing:queue_id")?.[1]

    return {
        isAutoTranslateEnabled,
        operatorLanguage,
        queueId: contextQueueId,
        languageContextElement: languageContextElement,
        rawContext: context
    }
}
